import { useState } from 'react';
import { Download, X, Share, PlusSquare, Smartphone } from 'lucide-react';
import { usePwaInstall } from '../hooks/usePwaInstall';
import ModalPortal from './ModalPortal';

export default function PwaInstallBanner() {
  const { canPrompt, isIosDevice, install, dismiss, hasNativePrompt } = usePwaInstall();
  const [showIosGuide, setShowIosGuide] = useState(false);

  if (!canPrompt) return null;

  const handleInstall = async () => {
    if (hasNativePrompt) {
      await install();
    } else if (isIosDevice) {
      setShowIosGuide(true);
    }
  };

  return (
    <>
      <div className="bg-gradient-to-r from-[#1a1a2e] to-[#16213e] text-white px-4 py-3 flex items-center justify-between gap-3 animate-slide-up">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-[#F598AF]/20 flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-5 h-5 text-[#F598AF]" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold leading-tight">Installeer Pracht</p>
            <p className="text-xs text-white/80 leading-tight mt-0.5">
              {isIosDevice
                ? 'Voeg toe aan beginscherm voor push notificaties'
                : 'Installeer de app voor push notificaties op je telefoon'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handleInstall}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-brand text-white text-sm font-semibold rounded-lg hover:bg-brand-active transition-colors"
          >
            <Download className="w-3.5 h-3.5" />
            Installeer
          </button>
          <button
            onClick={dismiss}
            className="p-1.5 text-white/70 hover:text-white transition-colors rounded-lg hover:bg-white/10"
            aria-label="Sluiten"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {showIosGuide && (
        <ModalPortal>
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-surface w-full sm:max-w-sm sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden animate-slide-up">
            <div className="px-5 pt-5 pb-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-foreground">App installeren op iOS</h3>
                <button
                  onClick={() => setShowIosGuide(false)}
                  className="p-1 text-foreground-tertiary hover:text-foreground-secondary rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <p className="text-sm text-foreground-tertiary mb-5">
                Volg deze stappen om Pracht te installeren en push notificaties te ontvangen:
              </p>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-blue-600">1</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">Tik op het Deel-icoon</p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <Share className="w-4 h-4 text-blue-500" />
                      <p className="text-xs text-foreground-tertiary">Onderaan in Safari</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-blue-600">2</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">Kies "Zet op beginscherm"</p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <PlusSquare className="w-4 h-4 text-blue-500" />
                      <p className="text-xs text-foreground-tertiary">Scroll naar beneden in het menu</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-green-600">3</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">Tik op "Voeg toe"</p>
                    <p className="text-xs text-foreground-tertiary mt-1">Open de app daarna vanaf je beginscherm</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-5 py-4 bg-surface-secondary border-t border-theme-border">
              <button
                onClick={() => setShowIosGuide(false)}
                className="w-full py-2.5 bg-brand text-white text-sm font-semibold rounded-lg hover:bg-brand-active transition-colors"
              >
                Begrepen
              </button>
            </div>
          </div>
        </div>
        </ModalPortal>
      )}
    </>
  );
}
